// EN-1862: the layout header grows while the new version banner shows, so
// things that sit below it read the real height from a CSS variable instead of
// a hardcoded 50px.
export const HEADER_HEIGHT_VAR = '--app-header-height';
export const DEFAULT_HEADER_HEIGHT_PX = 50;

/** CSS length: the real header height (50px until measured) plus extraPx. */
export function headerOffset(extraPx = 0): string {
  return `calc(var(${HEADER_HEIGHT_VAR}, ${DEFAULT_HEADER_HEIGHT_PX}px) + ${extraPx}px)`;
}

interface HeightSource {
  getBoundingClientRect(): { height: number };
}

interface StyleTarget {
  style: {
    setProperty(name: string, value: string): void;
    removeProperty(name: string): unknown;
  };
}

interface Observer {
  observe(el: unknown): void;
  disconnect(): void;
}

type ObserverConstructor = new (callback: () => void) => Observer;

/**
 * Publishes the header's height as a CSS variable on root, now and whenever it
 * changes (for example when the banner appears). Returns a function that stops
 * watching and removes the variable.
 */
export function syncHeaderHeight(
  header: HeightSource | null | undefined,
  root: StyleTarget,
  ObserverImpl:
    | ObserverConstructor
    | null
    | undefined = typeof ResizeObserver === 'undefined'
    ? undefined
    : (ResizeObserver as unknown as ObserverConstructor)
): () => void {
  if (!header) return () => undefined;
  const measure = () => {
    const height = header.getBoundingClientRect().height;
    if (height > 0) root.style.setProperty(HEADER_HEIGHT_VAR, `${height}px`);
  };
  measure();
  const observer = ObserverImpl ? new ObserverImpl(measure) : null;
  observer?.observe(header);
  return () => {
    observer?.disconnect();
    root.style.removeProperty(HEADER_HEIGHT_VAR);
  };
}
